import { memo } from "react";

import { useIntl } from "react-intl";

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from "@/components/molecules/Popover";
import Close20 from "@/icons/__generated__/X/X20";
import type { AwardsWinnerBadgeProps } from "~/components/gallery/photo-gallery.types";
import { AppModalKey, useAppModal } from "~/modals";

import {
  getAwardsWinnerCategory,
  getAwardsWinnerYear,
} from "./AwardsWinnerBadge.utils";
import AwardsWinnerBadgeIcon from "./AwardsWinnerBadgeIcon";
import AwardsWinnerContent from "./AwardsWinnerContent";

/**
 * Houses the logic for the Awards Winner Badge component.
 * This component is used to display the Awards Winner Badge on the listing page.
 */
export const AwardsWinnerBadge = ({
  isMDScreen,
  listingAwards,
  listingCity,
  listingCountry,
  listingUseType,
}: AwardsWinnerBadgeProps) => {
  const intl = useIntl();

  const year = getAwardsWinnerYear(listingAwards);

  const linkLocale =
    listingCountry === "US" ? "" : `/${listingCountry.toLowerCase()}`;

  const category = getAwardsWinnerCategory(listingAwards, intl);

  const { openModal: openAwardsWinnerDrawer } = useAppModal(
    AppModalKey.ListingAwardsWinnerDrawer,
  );

  const handleBadgeClick = () => {
    openAwardsWinnerDrawer({
      category,
      linkLocale,
      listingAwards,
      listingCity,
      listingUseType,
      year,
    });
  };

  return (
    <div className="tw-group">
      {!isMDScreen ? (
        <button
          type="button"
          data-testid="listing-awards-winner-button"
          onClick={handleBadgeClick}
        >
          <AwardsWinnerBadgeIcon year={year} country={listingCountry} />
        </button>
      ) : (
        <Popover>
          <PopoverTrigger>
            <AwardsWinnerBadgeIcon year={year} country={listingCountry} />
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={16}
            className="tw-max-w-[320px]"
          >
            <AwardsWinnerContent
              year={year}
              listingUseType={listingUseType}
              listingAwards={listingAwards}
              listingCity={listingCity}
              category={category}
              linkLocale={linkLocale}
              closeButton={
                <PopoverClose aria-label="close" asChild>
                  <Close20 className="tw-self-end tw-text-base-white tw-cursor-pointer tw-mb-12" />
                </PopoverClose>
              }
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default memo(AwardsWinnerBadge);
